import React, { useState } from "react";

import {
  Container,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Chip
} from "@mui/material";
import { useFetchCommunityReview } from "./hooks/useFetchCommunityReview";
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import DetailsSkeleton from "../ui/DetailSkeleton";
import BooleanChip from "../ui/BooleanChip";
import StatusChip from "../ui/StatusChip";
import { COLORS } from "../../constants/colors";
import { CommunityReviewDrawer } from "./Drawer";
import ArrowBack from "@mui/icons-material/ArrowBack";

const formatDate = (date) => {
  if (!date) {
    return "-";
  }

  const dateInstance = new Date(date);
  const options = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    year: "numeric",
    month: "numeric",
    day: "numeric"
  };

  return new Intl.DateTimeFormat("default", options).format(dateInstance);
};

export const CommunityReviewDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data: review, isLoading } = useFetchCommunityReview({ id });

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          <Box display="flex" gap="16px">
            <NavLink to={`/community-reviews?${searchParams}`} paddingTop="4px">
              <ArrowBack
                sx={{
                  fontSize: "28px",
                  color: COLORS.BLACK
                }}
              />
            </NavLink>

            <Typography variant="h1" fontWeight="bold" sx={{ mb: 2 }}>
              {id}
            </Typography>
          </Box>
          <Box display="flex" gap="8px">
            <Typography
              color="primary"
              variant="body2"
              sx={{ cursor: "pointer" }}
              onClick={() => setIsDrawerOpen(true)}
            >
              Update
            </Typography>
          </Box>
        </Box>

        <Box component={Paper} flexGrow={1} p={3}>
          {isLoading && <DetailsSkeleton />}
          {review && (
            <>
              <Box mb={3} sx={{ width: "100%", overflow: "auto" }}>
                <Typography variant="h4">Details</Typography>
                <List>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        ID
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>{review.id}</Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        User
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      {review.userName}
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Brand
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      {review.brandName}
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Recommended?
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      <BooleanChip value={review.isRecommended} />
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Tags
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: "calc(100% - 15rem)" }}
                      display="flex"
                      gap="8px"
                    >
                      {review.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          value={tag}
                          variant="filled"
                          size="small"
                          color="primary"
                          sx={{
                            color: "white"
                          }}
                        />
                      ))}
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Content
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      {review.content}
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Status
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      <StatusChip
                        value={review.status}
                        colorMap={{
                          Pending: COLORS.GRAY,
                          Published: COLORS.PERIWINKLE,
                          Rejected: COLORS.ERROR_LIGHT
                        }}
                      />
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Created At
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      {formatDate(review.createdAt)}
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Published At
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      {formatDate(review.publishedDate)}
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Transaction ID
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      {review.transactionId}
                    </Box>
                  </ListItem>
                  <ListItem
                    sx={{
                      borderBottom: "1px solid #ededed",
                      p: 2
                    }}
                  >
                    <Box sx={{ width: "15rem", display: "inline-block" }}>
                      <Typography variant="h5" fontWeight="bold">
                        Transaction Date
                      </Typography>
                    </Box>
                    <Box sx={{ width: "calc(100% - 15rem)" }}>
                      {formatDate(review.transactionDate)}
                    </Box>
                  </ListItem>
                </List>
              </Box>

              {review.reviewModeration && (
                <>
                  <Box mb={3} sx={{ width: "100%", overflow: "auto" }}>
                    <Typography variant="h4">Moderation Details</Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 500
                      }}
                    >
                      Review Moderation
                    </Typography>
                    <Box
                      backgroundColor="#f5f5f5"
                      padding="16px"
                      borderRadius="8px"
                    >
                      <pre>
                        <code
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word"
                          }}
                        >
                          {JSON.stringify(review.reviewModeration, null, 2)}
                        </code>
                      </pre>
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Container>
      {review && (
        <CommunityReviewDrawer
          review={review}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
    </>
  );
};
